import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import { useNavigate, Link, useLocation, useParams } from "react-router-dom";
import { useClickOutside } from "./useClickOutside";
import { House, Trophy, Store, ChevronDown, Dumbbell, Gift, Medal, GalleryHorizontalEnd, TrafficCone, Calendar } from "lucide-react";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

export default function Topbar({ showLoginButton, showBudget, showLinks, showEmptyRewards, reload }) {
  // eslint-disable-next-line no-unused-vars
  const { leagueId } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [budget, setBudget] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }

      const { data: profileRes } = await axios.get(`${apiBase}/api/profile/topbar`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        params: { leagueId },
      });

      setProfile(profileRes.profile);
      setBudget(profileRes.membership?.budget ?? null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  // Close dropdown when clicking outside
  useClickOutside(menuRef, () => setOpen(false));
  useClickOutside(dropdownRef, () => setTeamDropdownOpen(false));

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Logout error:", error.message);
    } else {
      navigate("/");
    }
  };

  const truncate = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };

  const formatNum = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "0";

    if (num >= 1_000_000) {
      return truncate(num / 1_000_000, 1) + "M";
    }

    if (num >= 1_000) {
      return truncate(num / 1_000, 1) + "K";
    }

    return truncate(num, 1).toFixed(1);
  };

  if (loading) return <Loader loadingTitle={"Loading profile"} showLogo={true} />;
  if (error) return <p>Error: {error}</p>;
  if (!profile) return <p>No profile data.</p>;

  return (
    <div style={{ borderBottom: "1px solid #19232f" }}>
      <div className="topbar">
        <div className="left-nav-box">
          <img src="/logo.svg" alt="logo" className="topbar-logo" />
          {/* Hamburger button (mobile only) */}
          <button className={`hamburger-btn ${mobileOpen ? "active" : ""}`} onClick={() => setMobileOpen(!mobileOpen)}>
            ☰
          </button>
          {!leagueId ? (
            <Link to={`/career`} className={location.pathname === `/career` ? "active-link" : ""} id="mobile-home-shortcut">
              <House size={24} />
            </Link>
          ) : (
            <Link
              to={`/dashboard/league/${leagueId}`}
              className={location.pathname === `/dashboard/league/${leagueId}` ? "active-link" : ""}
              id="mobile-home-shortcut">
              <House size={24} />
            </Link>
          )}

          {/* Mobile Nav (separate div) */}
          {mobileOpen && (
            <>
              <div className="nav-links-mobile">
                <img src="/logo.svg" alt="logo" />
                {leagueId && (
                  <>
                    <Link to={`/dashboard/league/${leagueId}`} className={location.pathname === `/dashboard/league/${leagueId}` ? "active-link" : ""}>
                      <House size={16} />
                      Dashboard
                    </Link>
                    <Link
                      to={`/dashboard/league/${leagueId}/prep`}
                      className={location.pathname === `/dashboard/league/${leagueId}/prep` ? "active-link" : ""}>
                      <img
                        src="/vs-vector.png"
                        style={{ width: "18px", opacity: location.pathname === `/dashboard/league/${leagueId}/prep` ? "0.4" : "1" }}
                        alt="VS"
                      />{" "}
                      Matchday Prep
                    </Link>
                    <Link to={`/league/${leagueId}/train`} className={location.pathname === `/league/${leagueId}/train` ? "active-link" : ""}>
                      <Dumbbell size={16} /> Practice
                    </Link>
                    <Link
                      to={`/dashboard/league/${leagueId}/collection`}
                      className={location.pathname === `/dashboard/league/${leagueId}/collection` ? "active-link" : ""}>
                      <GalleryHorizontalEnd size={16} /> Collection
                    </Link>
                    <Link
                      to={`/around-the-league/${leagueId}`}
                      className={location.pathname === `/around-the-league/${leagueId}` ? "active-link" : ""}>
                      <Trophy size={16} />
                      League Fixtures
                    </Link>
                    <Link
                      to={`/dashboard/league/${leagueId}/history`}
                      className={location.pathname === `/dashboard/league/${leagueId}/history` ? "active-link" : ""}>
                      <Calendar size={16} />
                      Results & Stats
                    </Link>
                    {/* <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Link to={`/league/${leagueId}/pickems`} className={location.pathname === `/league/${leagueId}/pickems` ? "active-link" : ""}>
                        <Dices size={16} />
                        Pick'em
                      </Link>
                    </div> */}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Link to={`/league/${leagueId}/market`} className={location.pathname === `/league/${leagueId}/market` ? "active-link" : ""}>
                        <Store size={16} />
                        Market
                      </Link>
                    </div>
                    <div className="sparkle-container" style={{ display: "flex", alignItems: "center", gap: " 4px" }}>
                      <img src="/star.svg" className="svg-star star1" alt="star" />
                      <img src="/star.svg" className="svg-star star2" alt="star" />
                      <img src="/star.svg" className="svg-star star3" alt="star" />
                      {/* <img src="/star.svg" class="svg-star star4" alt="star" /> */}
                      <img src="/star.svg" className="svg-star star5" alt="star" />
                      <img src="/star.svg" className="svg-star star6" alt="star" />
                      {/* <img src="/star.svg" class="svg-star star7" alt="star" /> */}
                      <Gift size={20} color="#e0b768" />
                      <Link
                        to={`/rewards/${leagueId}`}
                        style={{
                          backgroundImage: "linear-gradient(rgb(219 218 216), rgb(208 161 68))", // Standard gradient for modern browsers
                          WebkitBackgroundClip: "text", // WebKit-specific property
                          WebkitTextFillColor: "transparent", // WebKit-specific property
                          backgroundClip: "text", // Standard property for background-clip
                          color: "transparent", // Fallback for text fill color
                          padding: "4px 0",
                        }}
                        className={location.pathname === `/rewards/${leagueId}` ? "active-link" : ""}>
                        Rewards
                      </Link>
                    </div>
                    <Link to={`/leaderboard/yearly`} className={location.pathname === `/leaderboard/yearly` ? "active-link" : ""}>
                      <Medal size={16} />
                      Global Leaderboard
                    </Link>
                  </>
                )}
                {!leagueId && (
                  <>
                    <Link to="/profile/me" className={location.pathname === `/profile/me` ? "active-link" : ""}>
                      Profile
                    </Link>
                    <Link to="/career" className={location.pathname === `/career` ? "active-link" : ""}>
                      Career & Leagues
                    </Link>
                    <Link to={`/dashboard/collection`} className={location.pathname === `/dashboard/collection` ? "active-link" : ""}>
                      <GalleryHorizontalEnd size={16} /> Collection
                    </Link>
                    <Link to={`/leaderboard/yearly`} className={location.pathname === `/leaderboard/yearly` ? "active-link" : ""}>
                      <Medal size={16} />
                      Global Leaderboard
                    </Link>
                  </>
                )}
              </div>
            </>
          )}

          <nav aria-label="Main navigation">
            {showLinks && (
              <>
                <Link to={`/dashboard/league/${leagueId}`} className={location.pathname === `/dashboard/league/${leagueId}` ? "active-link" : ""}>
                  <House size={16} />
                  Dashboard
                </Link>
                <div className="dropdown" style={{ position: "relative" }} ref={dropdownRef}>
                  <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                    <Link onClick={() => setTeamDropdownOpen(!teamDropdownOpen)}>
                      <TrafficCone size={16} /> Team
                    </Link>
                    <ChevronDown size={16} color="white" />
                  </div>
                  {teamDropdownOpen && (
                    <div className={`profile-dropdown ${teamDropdownOpen ? "open" : ""}`}>
                      <Link
                        to={`/dashboard/league/${leagueId}/prep`}
                        className={location.pathname === `/dashboard/league/${leagueId}/prep` ? "active-link" : ""}>
                        <img
                          src="/vs-vector.png"
                          style={{ width: "18px", opacity: location.pathname === `/dashboard/league/${leagueId}/prep` ? "0.4" : "1" }}
                          alt="VS"
                        />
                        Matchday Prep
                      </Link>
                      <Link to={`/league/${leagueId}/train`} className={location.pathname === `/league/${leagueId}/train` ? "active-link" : ""}>
                        <Dumbbell size={16} /> Practice
                      </Link>
                      <Link
                        to={`/dashboard/league/${leagueId}/collection`}
                        className={location.pathname === `/dashboard/league/${leagueId}/collection` ? "active-link" : ""}>
                        <GalleryHorizontalEnd size={16} /> Collection
                      </Link>
                    </div>
                  )}
                </div>
                <Link to={`/around-the-league/${leagueId}`} className={location.pathname === `/around-the-league/${leagueId}` ? "active-link" : ""}>
                  <Trophy size={16} />
                  League Fixtures
                </Link>
                <Link
                  to={`/dashboard/league/${leagueId}/history`}
                  className={location.pathname === `/dashboard/league/${leagueId}/history` ? "active-link" : ""}>
                  <Calendar size={16} />
                  Results & Stats
                </Link>
                {/* <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Link to={`/league/${leagueId}/pickems`} className={location.pathname === `/league/${leagueId}/pickems` ? "active-link" : ""}>
                    <Dices size={16} />
                    Pick'em
                  </Link>
                </div> */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Link to={`/league/${leagueId}/market`} className={location.pathname === `/league/${leagueId}/market` ? "active-link" : ""}>
                    <Store size={16} />
                    Market
                  </Link>
                </div>
                <div className="sparkle-container" style={{ display: "flex", alignItems: "center", gap: " 4px" }}>
                  <img src="/star.svg" className="svg-star star1" alt="star" />
                  <img src="/star.svg" className="svg-star star2" alt="star" />
                  <img src="/star.svg" className="svg-star star3" alt="star" />
                  {/* <img src="/star.svg" class="svg-star star4" alt="star" /> */}
                  <img src="/star.svg" className="svg-star star5" alt="star" />
                  <img src="/star.svg" className="svg-star star6" alt="star" />
                  {/* <img src="/star.svg" class="svg-star star7" alt="star" /> */}
                  <Gift size={20} color="#e0b768" />
                  <Link
                    to={`/rewards/${leagueId}`}
                    style={{
                      backgroundImage: "linear-gradient(rgb(219 218 216), rgb(208 161 68))", // Standard gradient for modern browsers
                      WebkitBackgroundClip: "text", // WebKit-specific property
                      WebkitTextFillColor: "transparent", // WebKit-specific property
                      backgroundClip: "text", // Standard property for background-clip
                      color: "transparent", // Fallback for text fill color
                      padding: "4px 0",
                    }}
                    className={location.pathname === `/rewards/${leagueId}` ? "active-link" : ""}>
                    Rewards
                  </Link>
                </div>
              </>
            )}
            {!leagueId && (
              <>
                <Link to="/career" className={location.pathname === `/career` ? "active-link" : ""}>
                  Career & Leagues
                </Link>
                <Link to={`/dashboard/collection`} className={location.pathname === `/dashboard/collection` ? "active-link" : ""}>
                  <GalleryHorizontalEnd size={16} /> Collection
                </Link>
                <Link to={`/leaderboard/yearly`} className={location.pathname === `/leaderboard/yearly` ? "active-link" : ""}>
                  <Medal size={16} />
                  Global Leaderboard
                </Link>
              </>
            )}
          </nav>
        </div>

        <div style={{ display: "flex", gap: "20px" }}>
          <div className="profile-menu-wrapper" ref={menuRef}>
            <div className="profile-details-topbar">
              <div className="mobile-currency">
                {showBudget && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 12px",
                      border: "1px solid rgb(25, 35, 47)",
                      borderRadius: "6px",
                    }}>
                    <p style={{ fontSize: "14px", margin: "0" }}>${formatNum(budget)}</p>
                  </div>
                )}

                {/* {showEmptyRewards ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid rgb(41 51 64)",
                      borderRadius: "8px",
                      justifyContent: "space-between",
                    }}
                    onClick={() => navigate(`/rewards`)}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        borderRadius: "6px",
                      }}>
                      <img src="/credit-token.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                      <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>{profile.tokens}</p>
                    </div>
                    <h2
                      style={{
                        padding: "2px 8px",
                        margin: "0",
                        borderLeft: "1px solid rgb(25, 35, 47)",
                        background: "#3273fa",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                        fontWeight: "600",
                        boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                        color: "white",
                      }}>
                      +
                    </h2>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid rgb(41 51 64)",
                      borderRadius: "8px",
                      justifyContent: "space-between",
                    }}
                    onClick={() => navigate(`/rewards/${leagueId}`)}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        borderRadius: "6px",
                      }}>
                      <img src="/credit-token.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                      <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>{profile.tokens}</p>
                    </div>
                    <h2
                      style={{
                        padding: "2px 8px",
                        margin: "0",
                        borderLeft: "1px solid rgb(25, 35, 47)",
                        background: "#3273fa",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                        fontWeight: "600",
                        boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                        color: "white",
                      }}>
                      +
                    </h2>
                  </div>
                )} */}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 12px",
                    border: "1px solid rgb(25, 35, 47)",
                    borderRadius: "6px",
                  }}>
                  <img src="/xp-boost.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                  <p style={{ fontSize: "14px", margin: "0" }}>{profile.xp_boost}</p>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }} onClick={() => setOpen((prev) => !prev)}>
                <img src={`/avatars/${profile.profile_image}`} alt={profile.profile_name} />
                <ChevronDown size={16} />
              </div>
            </div>

            <div className={`profile-dropdown ${open ? "open" : ""}`}>
              <div className="currency-container">
                {showBudget && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 12px",
                      border: "1px solid rgb(41 51 64)",
                      borderRadius: "8px",
                    }}>
                    <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>${formatNum(budget)}</p>
                  </div>
                )}
                {/* {showEmptyRewards ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid rgb(41 51 64)",
                      borderRadius: "8px",
                      justifyContent: "space-between",
                    }}
                    onClick={() => navigate(`/rewards`)}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        borderRadius: "6px",
                      }}>
                      <img src="/credit-token.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                      <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>{profile.tokens}</p>
                    </div>
                    <h2
                      style={{
                        padding: "2px 8px",
                        margin: "0",
                        borderLeft: "1px solid rgb(25, 35, 47)",
                        background: "#3273fa",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                        fontWeight: "600",
                        boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                        color: "white",
                      }}>
                      +
                    </h2>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid rgb(41 51 64)",
                      borderRadius: "8px",
                      justifyContent: "space-between",
                    }}
                    onClick={() => navigate(`/rewards/${leagueId}`)}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        borderRadius: "6px",
                      }}>
                      <img src="/credit-token.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                      <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>{profile.tokens}</p>
                    </div>
                    <h2
                      style={{
                        padding: "2px 8px",
                        margin: "0",
                        borderLeft: "1px solid rgb(25, 35, 47)",
                        background: "#3273fa",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                        fontWeight: "600",
                        boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                        color: "white",
                      }}>
                      +
                    </h2>
                  </div>
                )} */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 12px",
                    border: "1px solid rgb(41 51 64)",
                    borderRadius: "8px",
                  }}>
                  <img src="/xp-boost.png" alt="tokens" style={{ height: "28px", width: "28px", border: "none", objectFit: "contain" }} />
                  <p style={{ fontSize: "14px", margin: "0", color: "white", fontWeight: "700" }}>{profile.xp_boost}</p>
                </div>
              </div>
              <Link to="/profile/me">Profile</Link>
              <Link to="/career">Career & Leagues</Link>
              <Link onClick={handleLogout}>Logout</Link>
            </div>
          </div>

          {showLoginButton && (
            <>
              <button
                onClick={() => navigate(`/signup`)}
                style={{
                  padding: "8px 20px",
                  cursor: "pointer",
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.14)",
                  color: "white",
                  borderRadius: "90px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}>
                Sign up
              </button>
              <button
                onClick={() => navigate(`/login`)}
                style={{
                  padding: "8px 14px",
                  border: "none",
                  cursor: "pointer",
                  background: "#3273FA",
                  color: "white",
                  borderRadius: "90px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}>
                Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
